'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ReviewTeaserCard,
  ReviewHubButton,
  ReviewHubPanel,
} from '@sifly/shared-ui/reviews'
import type { ReviewCardData } from '@sifly/shared-ui/content'
import {
  FOMO_LAST_SHOWN_KEY,
  FOMO_COUNT_KEY,
  FOMO_INDEX_KEY,
  FOMO_BACKOFF_KEY,
  isFomoEligible,
  nextRotationIndex,
  isRageClose,
  shouldSuppressPopup,
  parseStoredInt,
  TEASER_START,
  buildTeaserSequence,
  nextTeaserPhase,
  teaserPhaseDurationMs,
  teaserVisibleIndex,
  isTeaserFadingOut,
  type TeaserItem,
  type TeaserPhase,
} from '../../lib/reviewsFloatingCore'
import { useCommerceBarVisible } from '../../lib/commerceBar'

export type ReviewsFloatingHostProps = {
  popupEnabled: boolean
  hubEnabled: boolean
  /** SSR review pool (recent visible, capped). Empty → aggregate-only teaser. */
  reviews: ReviewCardData[]
  average: number
  count: number
  /** Dealer name — the teaser and the panel header both name the business. */
  companyName: string
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Storefront orchestrator for the reviews floating layer. Owns ALL side
 * effects — timers, localStorage frequency cap, DOM listeners, arbitration —
 * and delegates rendering to the dumb shared-ui surfaces. Pure decisions
 * (sequence contents, phase transitions, durations) live in
 * reviewsFloatingCore, which is unit-tested.
 *
 * Calm contract: after a quiet delay the teaser plays ONE short sequence —
 * the aggregate, then one rotated real review — then leaves nothing but the
 * indicator. It respects a cooldown, a lifetime cap, and a rage-close session
 * backoff, and one whole sequence counts as ONE impression. Everything lives
 * in a single bottom-left column so the teaser, the panel and the indicator
 * can never collide.
 */
export function ReviewsFloatingHost({
  popupEnabled,
  hubEnabled,
  reviews,
  average,
  count,
  companyName,
}: ReviewsFloatingHostProps) {
  const [hydrated, setHydrated] = useState(false)
  const reducedMotion = useRef(false)
  // A page (today only the PDP) declaring its sticky mobile commerce bar. The
  // column yields the corner while it's up — see the className below.
  const commerceBarVisible = useCommerceBarVisible()

  const [hubOpen, setHubOpen] = useState(false)
  const hubOpenRef = useRef(false)
  const [sequence, setSequence] = useState<TeaserItem[]>([])
  const [phase, setPhase] = useState<TeaserPhase>(TEASER_START)
  const shownAtRef = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)
  // Read the SSR pool via a ref so the one-shot start timer below never
  // re-arms (resetting its countdown) if the `reviews` prop identity changes.
  // average/count/companyName are mirrored the same way — they're stable
  // primitives from a Server Component in practice, but routing them through
  // the timer's dep array would re-arm (and reset) the countdown on any
  // change, contradicting the one-shot intent above.
  // Consequence: if `count` goes from 0 to positive after hydration without a
  // remount (a server-render refresh that mutates the layout's props in
  // place), the start effect below has already bailed out on its first run
  // (count <= 0) and nothing re-arms it, so the teaser silently skips that
  // visit. Accepted — this path isn't reachable via normal navigation.
  const reviewsRef = useRef(reviews)
  reviewsRef.current = reviews
  const averageRef = useRef(average)
  averageRef.current = average
  const countRef = useRef(count)
  countRef.current = count
  const companyNameRef = useRef(companyName)
  companyNameRef.current = companyName

  const hasPool = reviews.length > 0

  useEffect(() => {
    setHydrated(true)
    reducedMotion.current = prefersReducedMotion()
  }, [])

  // Keep a ref mirror of hubOpen so the (one-shot) start timer reads the live
  // value at fire time without re-arming on every toggle.
  useEffect(() => {
    hubOpenRef.current = hubOpen
  }, [hubOpen])

  // End the sequence. `manual` = the visitor clicked ✕ (eligible for the
  // rage-close session backoff); arbitration passes false. Tracked in a ref
  // (not state) so the focus-restore effect below can read it without being
  // in its own dependency array.
  const manualDismissRef = useRef(false)
  const endSequence = useCallback((manual: boolean) => {
    if (manual && shownAtRef.current > 0 && isRageClose(shownAtRef.current, Date.now())) {
      try {
        sessionStorage.setItem(FOMO_BACKOFF_KEY, '1')
      } catch {
        /* private mode — ignore */
      }
    }
    manualDismissRef.current = manual
    setPhase({ stage: 'done' })
  }, [])

  // Start trigger — one-shot timer after the calm delay. Runs the frequency
  // cap once for the WHOLE sequence, then hands off to the phase machine.
  useEffect(() => {
    if (!hydrated || !popupEnabled || countRef.current <= 0) return
    if (phase.stage !== 'idle') return
    const timer = window.setTimeout(() => {
      if (shouldSuppressPopup({ hubOpen: hubOpenRef.current })) {
        setPhase({ stage: 'done' })
        return
      }
      let lastShown: number | null = null
      let storedCount = 0
      let prevIndex: number | null = null
      let backoff = false
      try {
        lastShown = parseStoredInt(localStorage.getItem(FOMO_LAST_SHOWN_KEY))
        storedCount = parseStoredInt(localStorage.getItem(FOMO_COUNT_KEY)) ?? 0
        prevIndex = parseStoredInt(localStorage.getItem(FOMO_INDEX_KEY))
        backoff = sessionStorage.getItem(FOMO_BACKOFF_KEY) === '1'
      } catch {
        /* storage blocked — reads stay at their unset defaults, and the write
         * below throws too (caught separately), so the count never persists.
         * Not a crash: this visitor just replays the sequence once per full
         * page load instead of ever being capped or put on cooldown. */
      }
      if (!isFomoEligible({ now: Date.now(), lastShown, count: storedCount, backoff })) {
        setPhase({ stage: 'done' })
        return
      }

      const pool = reviewsRef.current
      const idx = nextRotationIndex(prevIndex, pool.length)
      const review = pool[idx] ?? null
      const items = buildTeaserSequence({
        companyName: companyNameRef.current,
        average: averageRef.current,
        count: countRef.current,
        review,
      })
      if (items.length === 0) {
        setPhase({ stage: 'done' })
        return
      }

      try {
        localStorage.setItem(FOMO_LAST_SHOWN_KEY, String(Date.now()))
        localStorage.setItem(FOMO_COUNT_KEY, String(storedCount + 1))
        localStorage.setItem(FOMO_INDEX_KEY, String(idx))
      } catch {
        /* ignore */
      }
      shownAtRef.current = Date.now()
      setSequence(items)
      setPhase({ stage: 'in', index: 0 })
    }, teaserPhaseDurationMs(TEASER_START, []))
    return () => window.clearTimeout(timer)
  }, [hydrated, popupEnabled, phase.stage])

  // Advance the sequence. One timer per phase; `done` and `idle` are inert
  // (idle is owned by the start effect above, which runs the frequency cap).
  useEffect(() => {
    if (phase.stage === 'idle' || phase.stage === 'done') return
    const timer = window.setTimeout(() => {
      setPhase((p) => nextTeaserPhase(p, sequence.length))
    }, teaserPhaseDurationMs(phase, sequence))
    return () => window.clearTimeout(timer)
  }, [phase, sequence])

  const openHub = useCallback(() => {
    // Arbitration: opening the hub ends any running sequence.
    endSequence(false)
    setHubOpen(true)
  }, [endSequence])

  const closeHub = useCallback(() => setHubOpen(false), [])

  // Close-path focus bookkeeping, declared ahead of the listeners that set it.
  const wasHubOpenRef = useRef(false)
  const restoreFocusOnCloseRef = useRef(true)

  // Esc + outside-click close the hub (host-owned; panel stays presentational).
  useEffect(() => {
    if (!hubOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setHubOpen(false)
    }
    function onPointer(e: MouseEvent) {
      const el = containerRef.current
      if (el && e.target instanceof Node && !el.contains(e.target)) {
        // The click the visitor just made is what closes the panel, and the
        // browser has already focused whatever they clicked. Suppress the
        // close-path focus restore below, or we'd yank focus off the input
        // or link they were reaching for and drop their keystrokes.
        restoreFocusOnCloseRef.current = false
        setHubOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [hubOpen])

  // Focus management: activating the teaser card unmounts it (openHub's
  // arbitration ends the sequence), which would otherwise drop keyboard
  // focus to <body> — the next Tab would restart at the top of the document
  // instead of continuing from the panel that just appeared. Move focus into
  // the panel whenever it opens, regardless of which control opened it.
  useEffect(() => {
    if (!hubOpen) return
    containerRef.current?.querySelector<HTMLElement>('[role="dialog"]')?.focus()
  }, [hubOpen])

  // Focus management: the panel took focus on open (above), so it must give
  // it back on close — Esc, outside click, and re-toggling the pill all
  // route through setHubOpen(false), and by the time this effect runs the
  // panel has already unmounted, so without this the browser drops focus to
  // <body> and the next Tab restarts at the top of the document. Fires only
  // on the true→false edge (tracked via a ref, not state, so mount and the
  // false→true open edge are both no-ops) and never overlaps the open-path
  // effect above, since the two conditions (`!hubOpen` vs `wasOpen &&
  // !hubOpen`) can't both be true for the same transition.
  //
  // Exception: an outside click already focused whatever the visitor clicked
  // before this passive effect runs, so onPointer clears the flag below and
  // we leave their target alone. Esc, the panel ✕, and re-toggling the pill
  // all keep the restore.
  useEffect(() => {
    const wasOpen = wasHubOpenRef.current
    wasHubOpenRef.current = hubOpen
    if (!wasOpen || hubOpen) return
    const restore = restoreFocusOnCloseRef.current
    restoreFocusOnCloseRef.current = true
    if (!restore) return
    // No indicator pill to return to (hub disabled, or an empty review
    // pool) — nothing sensible to focus, so leave it alone.
    containerRef.current?.querySelector<HTMLElement>('[aria-haspopup="dialog"]')?.focus()
  }, [hubOpen])

  // The ✕ on a teaser card unmounts it too, but ends the sequence with no
  // panel to focus (endSequence(true) never opens one). Best available
  // target: the indicator pill, when the dealer config renders one at this
  // location. If it doesn't (hub disabled, or an empty review pool), there
  // is nothing sensible to focus — leave it, focus falls back to <body>.
  useEffect(() => {
    if (phase.stage !== 'done' || !manualDismissRef.current) return
    manualDismissRef.current = false
    if (!(hubEnabled && hasPool)) return
    containerRef.current?.querySelector<HTMLElement>('[aria-haspopup="dialog"]')?.focus()
  }, [phase, hubEnabled, hasPool])

  if (!hydrated || count <= 0) return null

  const visibleIndex = teaserVisibleIndex(phase)
  const teaser = visibleIndex === null ? null : (sequence[visibleIndex] ?? null)
  // Single source of truth for whether the hub panel can actually render —
  // used below by the panel, the indicator button, and the teaser card's
  // click handler, so all three can never disagree.
  const panelCanOpen = hubEnabled && hasPool

  // z-[35] sits between the cookie banner (z-30) and the sticky commerce bar /
  // cart-drawer scrim (z-40) — the ladder documented in
  // docs/reference/storefront.md. It used to be z-40, tying with the commerce
  // bar and winning on DOM order, which is backwards: a review pill must never
  // paint over a buy button.
  //
  // The z-order alone isn't enough, because losing the stack would just bury
  // the pill under the bar. `hidden lg:flex` yields the corner outright while
  // the bar is up — and only below `lg`, exactly matching the bar's own
  // `lg:hidden`, so a desktop PDP (where the bar is in the DOM but not
  // visible) keeps its floater.
  return (
    <div
      ref={containerRef}
      className={`fixed bottom-4 left-4 z-[35] flex-col items-start gap-3 sm:bottom-6 sm:left-6 ${
        commerceBarVisible ? 'hidden lg:flex' : 'flex'
      }`}
    >
      {popupEnabled && teaser && !hubOpen && (
        // Out of flow, anchored above the column: a timer-driven mount that
        // sat IN the flex column resized the container and moved the
        // indicator pill — three real layout shifts per teaser sequence,
        // measured at ~0.08 CLS on the AE homepage (Lighthouse names this
        // exact node). Absolute positioning keeps the pill's geometry fixed
        // while the card plays; the card's own enter/exit is already
        // transform+opacity, which never shifts layout. (The hub panel stays
        // in flow — it opens only on click, which CLS excludes.)
        <div className="absolute bottom-full left-0 mb-3">
          {/* key = the card index. The card already unmounts during the `gap`
              phase (teaserVisibleIndex returns null there), so the next card
              mounts fresh regardless — this key is a guard that keeps that
              true even if the gap duration ever became zero. */}
          <ReviewTeaserCard
            key={visibleIndex}
            item={teaser}
            visible={!isTeaserFadingOut(phase)}
            reducedMotion={reducedMotion.current}
            onOpen={panelCanOpen ? openHub : undefined}
            onDismiss={() => endSequence(true)}
          />
        </div>
      )}

      {panelCanOpen && hubOpen && (
        <ReviewHubPanel
          reviews={reviews}
          average={average}
          count={count}
          companyName={companyName}
          onClose={closeHub}
        />
      )}

      {panelCanOpen && (
        <ReviewHubButton average={average} count={count} open={hubOpen} onClick={hubOpen ? closeHub : openHub} />
      )}
    </div>
  )
}
